<script>
	import { onMount } from 'svelte';
	import { fly } from 'svelte/transition';
	import { flowsAPI } from './api.js';
	import { toast } from './lib/toast.js';
	import Icon from './lib/Icon.svelte';
	import FlowEditor from './FlowEditor.svelte';
	import { FLOW_TEMPLATES, instantiate } from './lib/flowTemplates.js';

	let flows = $state([]);
	let loading = $state(true);
	let editingId = $state(null);
	let creating = $state(false);
	let statsFlow = $state(null);   // flow whose metrics modal is open
	let statsData = $state(null);
	let statsLoading = $state(false);

	const TRIGGER_LABEL = {
		manual: 'Manual',
		new_conversation: 'Conversación nueva',
		message_in: 'Mensaje entrante',
		keyword: 'Palabra clave'
	};

	async function openStats(f) {
		statsFlow = f;
		statsData = null;
		statsLoading = true;
		try {
			statsData = await flowsAPI.stats(f.id);
		} catch (e) {
			toast.error(e.message || 'Error cargando métricas');
		} finally {
			statsLoading = false;
		}
	}

	function fmtDuration(secs) {
		if (secs == null) return '—';
		if (secs < 60) return `${Math.round(secs)}s`;
		if (secs < 3600) return `${Math.round(secs / 60)} min`;
		return `${(secs / 3600).toFixed(1)} h`;
	}

	async function loadAll() {
		loading = true;
		try {
			flows = await flowsAPI.list();
		} catch (e) {
			toast.error(e.message || 'Error cargando flujos');
		} finally {
			loading = false;
		}
	}

	async function newFlow() {
		creating = true;
		try {
			const start_id = 'n_' + Date.now().toString(36);
			const flow = await flowsAPI.create({
				name: 'Nuevo flujo',
				is_active: false,
				trigger_type: 'new_conversation',
				trigger_config: {},
				start_node_id: start_id,
				nodes: {
					[start_id]: {
						id: start_id,
						type: 'message',
						config: { text: '¡Hola! 👋 Soy un bot.' },
						next: null
					}
				}
			});
			editingId = flow.id;
			toast.success('Flujo creado');
		} catch (e) {
			toast.error(e.message || 'No se pudo crear');
		} finally {
			creating = false;
		}
	}

	async function createFromTemplate(tpl) {
		if (creating) return;
		creating = true;
		try {
			const flow = await flowsAPI.create(instantiate(tpl));
			editingId = flow.id;
			toast.success(`Flujo "${tpl.name}" creado desde plantilla`);
		} catch (e) {
			toast.error(e.message || 'No se pudo crear desde la plantilla');
		} finally {
			creating = false;
		}
	}

	const TRIGGER_SHORT = {
		manual: 'Manual',
		message_in: 'Cada mensaje',
		new_conversation: 'Conversación nueva',
		keyword: 'Palabra clave'
	};

	async function toggle(f) {
		try {
			const updated = await flowsAPI.update(f.id, { is_active: !f.is_active });
			const idx = flows.findIndex(x => x.id === f.id);
			if (idx >= 0) flows[idx] = updated;
			flows = [...flows];
			toast.success(updated.is_active ? 'Flujo activado' : 'Flujo pausado');
		} catch (e) { toast.error(e.message || 'No se pudo actualizar'); }
	}

	async function duplicate(f) {
		try {
			await flowsAPI.duplicate(f.id);
			await loadAll();
			toast.success('Flujo duplicado');
		} catch (e) { toast.error(e.message || 'No se pudo duplicar'); }
	}

	async function remove(f) {
		if (!confirm(`¿Eliminar el flujo "${f.name}"?`)) return;
		try {
			await flowsAPI.remove(f.id);
			flows = flows.filter(x => x.id !== f.id);
			toast.success('Flujo eliminado');
		} catch (e) { toast.error(e.message || 'No se pudo eliminar'); }
	}

	function nodeCount(f) {
		return Object.keys(f.nodes || {}).length;
	}

	onMount(loadAll);
</script>

{#if editingId !== null}
	<FlowEditor
		flowId={editingId}
		onClose={() => { editingId = null; loadAll(); }}
	/>
{:else}
	<div class="flows-view">
		<div class="toolbar">
			<div class="left">
				<h2 class="title">Flujos (Salesbot)</h2>
				<p class="muted">Crea conversaciones automáticas con secuencia de pasos: mensajes, preguntas, condiciones, AI Agents y handoff.</p>
			</div>
			<button class="btn btn-primary" onclick={newFlow} disabled={creating}>
				<Icon name="plus" size={14} />
				Nuevo flujo
			</button>
		</div>

		<div class="body">
			{#if loading}
				<div class="loading"><div class="spinner"></div></div>
			{:else}
				<!-- Plantillas para empezar (siempre visibles) -->
				<section class="templates">
					<div class="tpl-head">
						<Icon name="sparkles" size={15} />
						<span>Plantillas para empezar</span>
						<small>Crea un flujo de ejemplo con 1 clic y edítalo a tu gusto.</small>
					</div>
					<div class="tpl-grid">
						{#each FLOW_TEMPLATES as tpl, i (tpl.id)}
							<button
								class="tpl-card"
								onclick={() => createFromTemplate(tpl)}
								disabled={creating}
								in:fly={{ y: 8, duration: 200, delay: i * 40 }}
							>
								<div class="tpl-icon"><Icon name={tpl.icon} size={18} /></div>
								<div class="tpl-text">
									<h4>{tpl.name}</h4>
									<p>{tpl.description}</p>
									<span class="tpl-trigger"><Icon name="circle" size={9} /> {TRIGGER_SHORT[tpl.trigger_type] || tpl.trigger_type}</span>
								</div>
								<span class="tpl-cta"><Icon name="plus" size={13} /> Usar</span>
							</button>
						{/each}
					</div>
				</section>
			{/if}

			{#if loading}
				<!-- spinner ya mostrado arriba -->
			{:else if !flows.length}
				<div class="empty card card-padded">
					<Icon name="zap" size={32} />
					<h3>Aún no tienes flujos propios</h3>
					<p class="muted">Usa una plantilla de arriba para empezar rápido, o crea uno desde cero.</p>
					<button class="btn btn-primary" onclick={newFlow}>
						<Icon name="plus" size={14} /> Crear flujo en blanco
					</button>
				</div>
			{:else}
				<div class="grid">
					{#each flows as f (f.id)}
						<article class="flow-card card">
							<header class="card-head">
								<div class="flow-icon"><Icon name="zap" size={18} /></div>
								<div class="flow-text">
									<h3>{f.name}</h3>
									{#if f.description}<p class="muted">{f.description}</p>{/if}
								</div>
								<span class="badge {f.is_active ? 'badge-success' : 'badge'}">
									{f.is_active ? 'Activo' : 'Borrador'}
								</span>
							</header>
							<div class="meta-row">
								<span class="meta-pill">
									<Icon name="circle" size={10} />
									{TRIGGER_LABEL[f.trigger_type] || f.trigger_type}
								</span>
								<span class="meta-pill">
									<Icon name="list" size={10} />
									{nodeCount(f)} {nodeCount(f) === 1 ? 'nodo' : 'nodos'}
								</span>
								<span class="meta-pill">
									<Icon name="trending-up" size={10} />
									{f.execution_count || 0} ejecuciones
								</span>
							</div>
							<footer class="card-foot">
								<button class="btn btn-ghost btn-sm" onclick={() => toggle(f)}>
									{f.is_active ? 'Pausar' : 'Activar'}
								</button>
								<button class="btn btn-ghost btn-sm" onclick={() => openStats(f)}>
									<Icon name="trending-up" size={13} /> Métricas
								</button>
								<button class="btn btn-ghost btn-sm" onclick={() => duplicate(f)}>
									<Icon name="plus" size={13} /> Duplicar
								</button>
								<button class="btn btn-ghost btn-sm danger" onclick={() => remove(f)}>
									<Icon name="trash" size={13} />
								</button>
								<button class="btn btn-primary btn-sm" onclick={() => (editingId = f.id)}>
									Editar
									<Icon name="arrow-right" size={13} />
								</button>
							</footer>
						</article>
					{/each}
				</div>
			{/if}
		</div>
	</div>
{/if}

{#if statsFlow}
	<div class="stats-backdrop" onclick={() => (statsFlow = null)} role="presentation">
		<div class="stats-modal" onclick={(e) => e.stopPropagation()} role="dialog">
			<header class="stats-head">
				<h3>Métricas — {statsFlow.name}</h3>
				<button class="btn btn-ghost btn-sm" onclick={() => (statsFlow = null)} aria-label="Cerrar">
					<Icon name="x" size={16} />
				</button>
			</header>
			{#if statsLoading}
				<div class="loading"><Icon name="loader" size={20} /></div>
			{:else if statsData}
				{#if !statsData.total_executions}
					<p class="muted" style="padding:16px 0;">Aún no hay ejecuciones de este flujo.</p>
				{:else}
					<div class="stats-cards">
						<div class="stat-card">
							<div class="stat-num">{statsData.total_executions}</div>
							<div class="stat-lbl">Ejecuciones</div>
						</div>
						<div class="stat-card">
							<div class="stat-num">{statsData.completion_rate ?? '—'}{statsData.completion_rate != null ? '%' : ''}</div>
							<div class="stat-lbl">Tasa de finalización</div>
						</div>
						<div class="stat-card">
							<div class="stat-num">{fmtDuration(statsData.avg_duration_seconds)}</div>
							<div class="stat-lbl">Duración promedio</div>
						</div>
					</div>
					<div class="stats-section">
						<h4>Por estado</h4>
						<div class="status-bars">
							{#each Object.entries(statsData.by_status) as [st, count]}
								<div class="status-row">
									<span class="status-name">{st}</span>
									<div class="status-bar">
										<div class="status-fill {st}" style="width:{Math.round(100 * count / statsData.total_executions)}%"></div>
									</div>
									<span class="status-count">{count}</span>
								</div>
							{/each}
						</div>
					</div>
					{#if statsData.drop_off?.length}
						<div class="stats-section">
							<h4>Dónde se quedan (abandono)</h4>
							<ul class="dropoff-list">
								{#each statsData.drop_off as d}
									<li><span class="dropoff-label">{d.label}</span><span class="dropoff-count">{d.count}</span></li>
								{/each}
							</ul>
						</div>
					{/if}
				{/if}
			{/if}
		</div>
	</div>
{/if}

<style>
	.flows-view {
		height: 100%;
		display: flex; flex-direction: column;
		background: var(--surface-1);
	}
	.toolbar {
		display: flex; justify-content: space-between; align-items: flex-start;
		gap: 16px;
		padding: 18px 24px;
		background: var(--surface-0);
		border-bottom: 1px solid var(--border-1);
		flex-shrink: 0;
	}
	.title { font-size: 17px; margin-bottom: 4px; }
	.muted { color: var(--text-3); font-size: 12.5px; line-height: 1.5; max-width: 600px; margin: 0; }
	.body { flex: 1; overflow-y: auto; padding: 18px 24px 24px; }
	.loading { display: flex; align-items: center; justify-content: center; padding: 60px; }
	.spinner {
		width: 32px; height: 32px;
		border: 3px solid var(--border-1);
		border-top-color: var(--accent);
		border-radius: 50%;
		animation: spin 700ms linear infinite;
	}
	@keyframes spin { to { transform: rotate(360deg); } }
	.empty {
		max-width: 520px; margin: 60px auto 0;
		text-align: center;
		display: flex; flex-direction: column; align-items: center; gap: 10px;
		color: var(--text-3);
	}
	.empty h3 { color: var(--text-1); margin-top: 4px; }

	/* Plantillas */
	.templates { margin-bottom: 22px; }
	.tpl-head {
		display: flex; align-items: center; gap: 8px;
		margin-bottom: 12px;
		color: var(--text-1); font-weight: 600; font-size: 13.5px;
	}
	.tpl-head small { color: var(--text-3); font-weight: 400; font-size: 12px; margin-left: 4px; }
	.tpl-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
		gap: 12px;
	}
	.tpl-card {
		display: flex; align-items: flex-start; gap: 11px;
		padding: 14px;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 10px;
		text-align: left;
		cursor: pointer;
		transition: border-color var(--transition-fast), box-shadow var(--transition-fast), transform var(--transition-fast);
	}
	.tpl-card:hover:not(:disabled) {
		border-color: var(--accent);
		box-shadow: var(--shadow-sm);
		transform: translateY(-1px);
	}
	.tpl-card:disabled { opacity: 0.6; cursor: default; }
	.tpl-icon {
		width: 34px; height: 34px; flex-shrink: 0;
		border-radius: 8px;
		background: var(--accent-soft); color: var(--accent);
		display: flex; align-items: center; justify-content: center;
	}
	.tpl-text { flex: 1; min-width: 0; }
	.tpl-text h4 { font-size: 13.5px; margin: 0 0 3px; color: var(--text-1); }
	.tpl-text p { font-size: 12px; line-height: 1.4; color: var(--text-3); margin: 0 0 7px; }
	.tpl-trigger {
		display: inline-flex; align-items: center; gap: 4px;
		font-size: 10.5px; font-weight: 600;
		color: var(--text-2); background: var(--surface-2);
		padding: 2px 7px; border-radius: 4px;
	}
	.tpl-cta {
		display: inline-flex; align-items: center; gap: 4px;
		font-size: 11.5px; font-weight: 600; color: var(--accent);
		flex-shrink: 0;
	}

	.grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: 14px;
	}
	.flow-card {
		padding: 16px;
		display: flex; flex-direction: column;
		gap: 12px;
	}
	.card-head { display: flex; align-items: flex-start; gap: 12px; }
	.flow-icon {
		width: 36px; height: 36px;
		border-radius: 8px;
		background: var(--accent-soft); color: var(--accent);
		display: flex; align-items: center; justify-content: center;
		flex-shrink: 0;
	}
	.flow-text { flex: 1; min-width: 0; }
	.flow-text h3 { font-size: 14px; margin-bottom: 2px; }
	.flow-text p { font-size: 12px; line-height: 1.4; }

	.meta-row { display: flex; flex-wrap: wrap; gap: 6px; }
	.meta-pill {
		display: inline-flex; align-items: center; gap: 5px;
		padding: 3px 8px;
		font-size: 11px; font-weight: 600;
		background: var(--surface-2); color: var(--text-2);
		border-radius: 4px;
	}

	.card-foot {
		display: flex; gap: 4px;
		justify-content: flex-end;
		flex-wrap: wrap;
	}
	.btn-ghost.danger { color: var(--danger); }
	.btn-ghost.danger:hover { background: var(--danger-soft); }

	/* ===== Stats / funnel modal ===== */
	.stats-backdrop {
		position: fixed; inset: 0; z-index: 80;
		background: rgba(15, 23, 42, 0.45);
		display: flex; align-items: center; justify-content: center;
		padding: 20px;
	}
	.stats-modal {
		background: var(--surface-0); border-radius: 14px;
		width: 100%; max-width: 560px; max-height: 86vh; overflow-y: auto;
		box-shadow: var(--shadow-xl); padding: 18px 20px 24px;
	}
	.stats-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
	.stats-head h3 { font-size: 16px; margin: 0; }
	.stats-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 18px; }
	.stat-card { background: var(--surface-1); border-radius: 10px; padding: 14px 10px; text-align: center; }
	.stat-num { font-size: 22px; font-weight: 700; color: var(--text-1); }
	.stat-lbl { font-size: 11.5px; color: var(--text-3); margin-top: 4px; }
	.stats-section { margin-bottom: 16px; }
	.stats-section h4 { font-size: 13px; color: var(--text-2); margin: 0 0 8px; }
	.status-bars { display: flex; flex-direction: column; gap: 6px; }
	.status-row { display: flex; align-items: center; gap: 8px; font-size: 12px; }
	.status-name { width: 84px; text-transform: capitalize; color: var(--text-2); }
	.status-bar { flex: 1; height: 8px; background: var(--surface-2, #eef1f6); border-radius: 4px; overflow: hidden; }
	.status-fill { height: 100%; background: var(--text-3); }
	.status-fill.completed { background: var(--success); }
	.status-fill.aborted { background: var(--warning); }
	.status-fill.error { background: var(--danger); }
	.status-fill.running, .status-fill.waiting { background: var(--info); }
	.status-count { width: 36px; text-align: right; color: var(--text-2); }
	.dropoff-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
	.dropoff-list li { display: flex; justify-content: space-between; gap: 10px; font-size: 12.5px; padding: 6px 10px; background: var(--surface-1); border-radius: 6px; }
	.dropoff-label { color: var(--text-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.dropoff-count { color: var(--text-1); font-weight: 600; }
</style>
